/**
 * OnlineAppPdfPreview — side-panel preview of the imported JotForm Application
 * PDF, shown while the FSR reviews/curates a cart that was imported from an
 * online application (Bill 2026-07-01).
 *
 * Fetches the PDF as a Blob through the api-client (auth headers attached) and
 * draws it with the bundled pdf.js (PdfCanvasViewer, no CDN). CHG-0097: it used
 * to go to a native <iframe>, which is an empty grey box on bank PCs (the
 * built-in PDF viewer is off by group policy, inside Teams too).
 *
 * Docks on the right as a resizable-width drawer so the FSR can eyeball the
 * source application against the pre-filled cart side-by-side.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/primitives';
import { PdfCanvasViewer } from '@/components/common/PdfCanvasViewer';
import { useApi } from '../hooks/useApi';
import './OnlineAppPdfPreview.css';

interface Props {
  cartId: string;
  onClose: () => void;
}

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; blob: Blob }
  | { kind: 'error'; message: string };

export function OnlineAppPdfPreview({ cartId, onClose }: Props): JSX.Element {
  const { client, makeSignal } = useApi();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const signal = makeSignal();
    setState({ kind: 'loading' });
    client.retail.onlineApplications
      .importedPdfBlob(cartId, { signal })
      .then(
        (blob): void => {
          if (cancelled) return;
          // (Bill import walk 2026-07-10, record 7592) — the pane rendered
          // EMPTY with no error. Silent-blank guard #1: a non-PDF payload
          // (e.g. a JSON/HTML error body that still arrived as 2xx) feeds the
          // iframe nothing visible. Surface it as a real error instead.
          const type = (blob.type ?? '').toLowerCase();
          if (blob.size === 0 || (type !== '' && !type.includes('pdf'))) {
            setState({
              kind: 'error',
              message: blob.size === 0
                ? 'The imported application PDF came back empty. Re-open the cart to retry; if it persists, re-import the application.'
                : `The server returned "${blob.type}" instead of a PDF. Re-open the cart to retry; if it persists, re-import the application.`,
            });
            return;
          }
          setState({ kind: 'ok', blob });
        },
        (err: unknown): void => {
          if (cancelled) return;
          if (err instanceof Error && err.name === 'AbortError') return;
          setState({
            kind: 'error',
            message:
              err instanceof Error ? err.message : 'No imported application PDF is available for this cart.',
          });
        },
      );
    return () => {
      cancelled = true;
    };
    // client + makeSignal stable; re-fetch only when the cart changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  return (
    <aside className="rao-pdf-preview" aria-label="Imported application PDF">
      <header className="rao-pdf-preview__header">
        <span className="rao-pdf-preview__title">📄 Online application</span>
        <button
          type="button"
          className="rao-pdf-preview__close"
          onClick={onClose}
          aria-label="Close application preview"
        >
          ✕
        </button>
      </header>

      <div className="rao-pdf-preview__body">
        {state.kind === 'loading' && (
          <div className="rao-pdf-preview__msg">Loading application…</div>
        )}
        {state.kind === 'error' && (
          <div className="rao-pdf-preview__msg rao-pdf-preview__msg--error">
            {state.message}
          </div>
        )}
        {state.kind === 'ok' && (
          <PdfCanvasViewer
            className="rao-pdf-preview__frame"
            source={state.blob}
            title="Imported online application PDF"
          />
        )}
      </div>
    </aside>
  );
}
